/**
 * Where a kanban card's "⋯" menu goes, in VIEWPORT coordinates. Pure — no DOM
 * access — so the clamping rules are unit-tested rather than eyeballed.
 *
 * WHY THE MENU IS NOT ABSOLUTELY POSITIONED IN THE CARD ANY MORE. Each kanban
 * column scrolls its own cards (`.status-column-cards`, `overflow-y: auto`),
 * and a scroll container CLIPS its descendants however high their z-index is.
 * The menu opens from the card's bottom-right corner, so on the last card of a
 * column — or on any card in a narrow column — most of it was cut off at the
 * column edge and the "Archive board" row was simply unreachable. The menu now
 * renders in the body-level portal layer as `position: fixed` and this decides
 * where, from the button's measured rect.
 */

/** A rect in viewport CSS px — what `getBoundingClientRect()` hands back. */
export interface AnchorRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MenuSize {
  width: number;
  height: number;
}

export interface ViewportSize {
  width: number;
  height: number;
}

/** Gap between the "⋯" button and the menu it opens. */
export const CARD_MENU_GAP_PX = 4;
/** How close to a viewport edge the menu may sit. */
export const CARD_MENU_MARGIN_PX = 8;

export interface CardMenuPosition {
  left: number;
  top: number;
  /** Which side of the button the menu opened on — the caller may style it. */
  placement: "below" | "above";
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/**
 * Below the button and right-aligned with it (the button sits at the card's
 * right edge, so a left-aligned menu would hang off the column), flipping
 * ABOVE when the menu would run past the viewport bottom — which is the common
 * case for the last card of a full column.
 *
 * When NEITHER side fits — a menu taller than the viewport, on a short window —
 * it stays below and is clamped into view rather than flipped: a clamped menu
 * whose top rows are readable beats one pinned to the top edge with its anchor
 * off-screen.
 */
export function placeCardMenu(
  anchor: AnchorRect,
  menu: MenuSize,
  viewport: ViewportSize,
): CardMenuPosition {
  const below = anchor.top + anchor.height + CARD_MENU_GAP_PX;
  const above = anchor.top - CARD_MENU_GAP_PX - menu.height;

  const fitsBelow = below + menu.height <= viewport.height - CARD_MENU_MARGIN_PX;
  const fitsAbove = above >= CARD_MENU_MARGIN_PX;
  const placement: "below" | "above" = fitsBelow || !fitsAbove ? "below" : "above";

  const wanted = placement === "below" ? below : above;
  // `Math.max` on the upper bound: with a menu taller than the viewport the
  // clamp range inverts, and the top margin has to win (the first rows are
  // the ones worth seeing).
  const top = clamp(
    wanted,
    CARD_MENU_MARGIN_PX,
    Math.max(CARD_MENU_MARGIN_PX, viewport.height - CARD_MENU_MARGIN_PX - menu.height),
  );

  const left = clamp(
    anchor.left + anchor.width - menu.width,
    CARD_MENU_MARGIN_PX,
    Math.max(CARD_MENU_MARGIN_PX, viewport.width - CARD_MENU_MARGIN_PX - menu.width),
  );

  return { left, top, placement };
}
